import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BookOpen, ArrowRight, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

//const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL = 'https://starnivesh.onrender.com';

export default function BookSummaries() {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchSummaries();
  }, []);

  const fetchSummaries = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/book-summaries`);
      //const response = await axios.get(`https://starnivesh.onrender.com/api/book-summaries`);
      setSummaries(response.data);
    } catch (error) {
      console.error("Error fetching book summaries:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      fetchSummaries();
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/book-summaries/search/${encodeURIComponent(query)}`);
      setSummaries(response.data);
    } catch (error) {
      console.error("Error searching book summaries:", error);
    } finally {
      setSearching(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <p className="text-text-secondary text-xl" data-testid="summaries-loading">Loading summaries...</p>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen" style={{backgroundColor: '#2C3540'}}>
      <section className="py-20" style={{backgroundColor: '#1F2937'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-16">
            <div>
              <h1 className="font-heading text-5xl md:text-6xl font-bold text-text-primary mb-4" data-testid="summaries-title">
                Book <span style={{color: "#F5A623"}}>Summaries</span>
              </h1>
              <p className="text-xl text-text-secondary max-w-2xl">
                Distilled wisdom from the best investment and finance books. Learn key concepts without spending hours reading.
              </p>
            </div>

            {/* Search Bar - Top Right */}
            <div className="w-full md:w-96 relative flex-shrink-0">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-muted h-5 w-5" />
              <Input
                type="text"
                placeholder="Search summaries..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                //className="pl-12 py-3 text-base bg-background-paper border-white/10 text-text-primary placeholder:text-text-muted"
                className="pl-12 py-3 text-base bg-gray-800 border-gray-50 text-text-primary placeholder:text-text-muted"
                data-testid="summaries-search-input"
              />
              {searching && <p className="text-text-muted text-sm mt-1">Searching...</p>}
              {searchQuery && !searching && (
                <p className="text-text-secondary text-sm mt-1">
                  {summaries.length} result{summaries.length !== 1 ? 's' : ''} found
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {summaries.map((summary, index) => (
              <Link key={summary.id} to={`/book-summaries/${summary.slug}`} data-testid={`summary-card-${index}`}>
                <div className="glass-effect rounded-2xl overflow-hidden hover:-translate-y-1 transition-all group h-full flex flex-col">
                  <div className="relative h-56 overflow-hidden bg-background-dark flex items-center justify-center">
                    <BookOpen className="h-24 w-24 text-primary/30" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent"></div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="font-heading text-2xl font-bold text-text-primary mb-2 group-hover:text-primary transition-colors">
                      {summary.book_title}
                    </h3>
                    <p className="text-text-muted mb-4">by {summary.author}</p>
                    <div className="flex-1">
                      <p className="text-text-secondary text-sm mb-3">Key Takeaways:</p>
                      <ul className="space-y-1">
                        {summary.key_takeaways.slice(0, 3).map((takeaway, idx) => (
                          <li key={idx} className="text-text-secondary text-sm flex items-start">
                            <span className="text-secondary mr-2">•</span>
                            <span className="line-clamp-1">{takeaway}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <span className="text-primary font-semibold flex items-center mt-4">
                      Read Summary <ArrowRight className="ml-2 h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}